/**
 * Reverse Amortization Calculation Utility
 *
 * Calculates interest payments for existing debt using reverse amortization.
 * Processes payments in reverse order, accumulating interest backwards from final payment.
 *
 * Formula: Interest Payment = Principal Interest (for this payment) + Interest Payment (from next payment)
 * Even if principal is zero, we still accumulate interest from future payments.
 *
 * Uses Decimal.js-light for all calculations to prevent floating-point errors.
 */

import Decimal from 'decimal.js-light';
import { EXISTING_DEBT_CALCULATIONS } from '~/config/existing-debt-constants';
import type { ExistingDebtPaymentForm } from '~/types';

/**
 * Calculate reverse amortization for existing debt payments
 * Processes payments in reverse order, accumulating interest
 *
 * @param payments - Array of payment forms with principal and interest rate
 * @returns Map of payment index to calculated interest payment amount
 */
export const calculateReverseAmortization = (
  payments: ExistingDebtPaymentForm[]
): Map<number, number> => {
  const calculatedPayments = new Map<number, number>();

  if (payments.length === 0) {
    return calculatedPayments;
  }

  // Process payments in reverse order (from last to first)
  for (let i = payments.length - 1; i >= 0; i--) {
    const payment = payments[i];

    // Parse numeric values properly from NumberInput
    // CurrencyInput stores as formatted string, need to clean it
    const principalStr = payment.principalPayment || '';
    const principal = parseFloat(principalStr.replace(/[^\d.-]/g, '')) || 0;

    const rateStr = payment.interestRate || '';
    const ratePercent = parseFloat(rateStr) || 0;
    const rateDecimal = ratePercent / 100; // Convert percentage to decimal

    // Calculate Principal Interest using Decimal to prevent floating-point errors
    // Uses 30/360 day count convention: semi-annual = rate / 2
    // Do the entire calculation in Decimal - no floating-point error ever occurs
    const principalInterest = new Decimal(principal)
      .times(rateDecimal)
      .dividedBy(EXISTING_DEBT_CALCULATIONS.SEMI_ANNUAL_DIVISOR)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber();

    // Get Interest Payment from the following payment (which we just calculated)
    // This is already rounded from the previous iteration
    const interestPaymentFromNext =
      i === payments.length - 1 ? 0 : calculatedPayments.get(i + 1) || 0;

    // Interest Payment = Principal Interest + Interest Payment from next payment
    // Use Decimal for the addition and round once at the end
    const interestPayment = new Decimal(principalInterest)
      .plus(interestPaymentFromNext)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber();

    calculatedPayments.set(i, interestPayment);
  }

  return calculatedPayments;
};
